import { useEffect, useMemo, useState } from 'react';
import type { CombatAbilityOption, CombatContentStatus, CombatKitData, CombatKitInput } from '../../shared/types';
import { ApiRequestError, api } from '../api';
import { CombatIconPicker } from '../components/CombatIconPicker';
import type { OptimizedItemIcon } from '../lib/item-icon';

const blank = (): CombatKitInput => ({
  technicalKey: 'kit.chaos',
  displayName: 'Chaos',
  description: '',
  lightAbilityId: null,
  heavyAbilityId: null,
  ultimateAbilityId: null,
  passiveAbilityIds: [],
  status: 'draft',
});

const slotDescriptions = {
  lightAbilityId: 'Häufig genutzter Standardangriff. Für freigegebene Kits erforderlich.',
  heavyAbilityId: 'Stärkere oder kontrollierende Fähigkeit mit bewussterem Einsatz.',
  ultimateAbilityId: 'Seltene, besonders starke Signaturfähigkeit des Kits.',
} satisfies Record<'lightAbilityId' | 'heavyAbilityId' | 'ultimateAbilityId', string>;

export function CombatKitEditorPage({
  id,
  onBack,
  onSaved,
  onMessage,
}: {
  id: string | null;
  onBack: () => void;
  onSaved: (id: string) => void;
  onMessage: (kind: 'success' | 'error', message: string) => void;
}) {
  const [draft, setDraft] = useState(blank());
  const [record, setRecord] = useState<CombatKitData | null>(null);
  const [abilities, setAbilities] = useState<CombatAbilityOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [fieldErrors,setFieldErrors]=useState<Record<string,string>>({});
  const [pendingIcon,setPendingIcon]=useState<OptimizedItemIcon|null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [options, loaded] = await Promise.all([api.abilityOptions(), id ? api.kit(id) : Promise.resolve(null)]);
        setAbilities(options.abilities);
        if (loaded) {
          setRecord(loaded.kit);
          const {
            id: _id,
            icon: _icon,
            lightAbility: _light,
            heavyAbility: _heavy,
            ultimateAbility: _ultimate,
            passiveAbilities: _passives,
            balance: _balance,
            revision: _revision,
            validation: _validation,
            unrealSync: _sync,
            createdAt: _created,
            updatedAt: _updated,
            ...editable
          } = loaded.kit;
          setDraft(editable);
        }
      } catch (error) {
        onMessage('error', error instanceof Error ? error.message : 'Kit konnte nicht geladen werden.');
      }
    })();
  }, [id]);

  function patch<K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setFieldErrors((current)=>{const next={...current};delete next[String(key)];return next;});
  }
  const fieldError=(field:string)=>fieldErrors[field] ?? Object.entries(fieldErrors).find(([key])=>key.startsWith(`${field}.`))?.[1] ?? null;

  async function save() {
    setBusy(true);
    setFieldErrors({});
    try {
      let saved = (id ? await api.updateKit(id, draft) : await api.createKit(draft)).kit;
      if (pendingIcon) {
        saved = (await api.uploadKitIcon(saved.id,pendingIcon.icon,pendingIcon.thumbnail)).kit;
        setPendingIcon(null);
      }
      setRecord(saved);
      onMessage('success', `Combat Kit „${saved.displayName}“ wurde gespeichert.`);
      onSaved(saved.id);
    } catch (error) {
      if (error instanceof ApiRequestError) setFieldErrors(Object.fromEntries(error.fieldErrors.map((entry)=>[entry.field,entry.message])));
      onMessage('error', error instanceof Error ? error.message : 'Kit konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  }

  const active = useMemo(() => abilities.filter((ability) => ability.form !== 'passive' && ability.isComboRoot), [abilities]);
  const passive = useMemo(() => abilities.filter((ability) => ability.form === 'passive'), [abilities]);
  const activeByDomain = useMemo(() => {
    const groups = new Map<string, CombatAbilityOption[]>();
    for (const ability of active) groups.set(ability.domainKey, [...(groups.get(ability.domainKey) ?? []), ability]);
    return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [active]);

  function abilityOption(ability: CombatAbilityOption) {
    const suffix = [ability.comboLength > 1 ? `${ability.comboLength}er Combo` : '', ability.status !== 'approved' ? ability.status : '', !ability.valid ? 'ungültig' : ''].filter(Boolean).join(' · ');
    return <option key={ability.id} value={ability.id}>{ability.displayName}{suffix ? ` · ${suffix}` : ''}</option>;
  }

  function selectedAbility(id: string | null) {
    return abilities.find((ability) => ability.id === id) ?? null;
  }

  function togglePassive(abilityId: string) {
    patch('passiveAbilityIds', draft.passiveAbilityIds.includes(abilityId)
      ? draft.passiveAbilityIds.filter((value) => value !== abilityId)
      : [...draft.passiveAbilityIds, abilityId]);
  }

  function slotCard(key: 'lightAbilityId' | 'heavyAbilityId' | 'ultimateAbilityId', label: string, required = false) {
    const selection = selectedAbility(draft[key]);
    return (
      <label className={`kit-slot-card ${selection ? 'filled' : ''}`}>
        <span className="kit-slot-heading"><strong>{label}</strong>{required && <small>Pflicht bei Freigabe</small>}</span>
        <span className="kit-slot-description">{slotDescriptions[key]}</span>
        <select aria-invalid={Boolean(fieldError(key))} value={draft[key] ?? ''} onChange={(event) => patch(key, event.target.value || null)}>
          <option value="">Noch nicht zugewiesen</option>
          {activeByDomain.map(([domain, entries]) => <optgroup key={domain} label={domain}>{entries.map(abilityOption)}</optgroup>)}
        </select>
        {fieldError(key)&&<small className="field-error">{fieldError(key)}</small>}
        {selection && <span className={`ability-selection-meta ${selection.valid ? '' : 'invalid'}`}><strong>{selection.displayName}</strong><small>{selection.domainKey} · Combo {selection.comboStage}/{selection.comboLength} · {selection.valid ? 'gültig' : 'noch ungültig'}</small></span>}
      </label>
    );
  }

  return (
    <div className="content-editor">
      <div className="page-heading editor-page-heading">
        <div>
          <button className="link-button" onClick={onBack}>← Combat Kits</button>
          <span className="eyebrow">Combat Kit</span>
          <h1>{id ? draft.displayName || 'Kit bearbeiten' : 'Neues Combat Kit'}</h1>
          <p>{record ? `Revision ${record.revision} · Unreal: ${record.unrealSync.state}` : 'Das Kit weist fertige Fähigkeiten ihren Spielslots zu.'}</p>
        </div>
        <div className="editor-heading-actions">
          <span className={`save-state ${draft.status}`}>{draft.status === 'draft' ? 'Entwurf' : draft.status === 'review' ? 'In Prüfung' : draft.status === 'approved' ? 'Freigegeben' : 'Archiviert'}</span>
          <button className="button primary" disabled={busy} onClick={() => void save()}>{busy ? 'Speichert …' : 'Speichern'}</button>
        </div>
      </div>

      {Object.keys(fieldErrors).length>0&&<section className="validation-panel"><div className="validation-error">Bitte prüfe die markierten Felder.</div></section>}

      {record && (record.validation.errors.length > 0 || record.validation.warnings.length > 0) && (
        <section className="validation-panel">
          {record.validation.errors.map((message) => <div className="validation-error" key={message}>{message}</div>)}
          {record.validation.warnings.map((message) => <div className="validation-warning" key={message}>{message}</div>)}
        </section>
      )}

      <section className="panel editor-panel">
        <div className="section-heading"><div><span className="eyebrow">Icon</span><h2>Kit-Icon</h2><p>Das Kit-Auswahlmenü verwendet dieses Bild; ohne Bild erscheint ein standardisierter Platzhalter.</p></div></div>
        <CombatIconPicker label="Kit-Icon" icon={record?.icon ?? null} value={pendingIcon} onChange={setPendingIcon}/>
      </section>

      <section className="panel editor-panel">
        <div className="section-heading"><div><span className="eyebrow">1 · Grunddaten</span><h2>Identität des Kits</h2><p>Name und Beschreibung erklären die Domänenfantasie; die technische ID bleibt dauerhaft stabil.</p></div></div>
        <div className="form-grid two wide-first">
          <label>Technische Kit-ID<input aria-invalid={Boolean(fieldError('technicalKey'))} className="technical-input" value={draft.technicalKey} disabled={Boolean(id)} spellCheck={false} onChange={(event) => patch('technicalKey', event.target.value.toLowerCase())}/>{fieldError('technicalKey')&&<small className="field-error">{fieldError('technicalKey')}</small>}<small className="field-help">Zum Beispiel kit.chaos oder kit.spirit.support.</small></label>
          <label>Name<input aria-invalid={Boolean(fieldError('displayName'))} value={draft.displayName} maxLength={100} placeholder="Zum Beispiel Chaos" onChange={(event) => patch('displayName', event.target.value)}/>{fieldError('displayName')&&<small className="field-error">{fieldError('displayName')}</small>}<small className="field-help">Sichtbarer Name im Auswahlmenü.</small></label>
        </div>
        <label>Beschreibung<textarea aria-invalid={Boolean(fieldError('description'))} rows={4} maxLength={1000} value={draft.description} placeholder="Spielstil, Schwerpunkt und Besonderheit des Kits beschreiben." onChange={(event) => patch('description', event.target.value)}/>{fieldError('description')&&<small className="field-error">{fieldError('description')}</small>}<small className="field-help field-counter">{draft.description.length}/1000 Zeichen</small></label>
        <div className="form-grid two compact-fields">
          <label>Status<select aria-invalid={Boolean(fieldError('status'))} value={draft.status} onChange={(event) => patch('status', event.target.value as CombatContentStatus)}><option value="draft">Entwurf</option><option value="review">In Prüfung</option><option value="approved">Freigegeben</option><option value="archived">Archiviert</option></select><small className="field-help">Freigabe ist erst möglich, wenn mindestens Light vollständig gültig ist.</small></label>
          <div className="field-callout">Heavy, Ultimate und passive Fähigkeiten können später ergänzt werden. Ein Entwurf darf deshalb bewusst unvollständig sein.</div>
        </div>
      </section>

      <section className="panel editor-panel">
        <div className="section-heading"><div><span className="eyebrow">2 · Aktive Slots</span><h2>Light, Heavy und Ultimate</h2><p>Es werden nur Combo-Startfähigkeiten angeboten. Die restlichen Combo-Stufen werden automatisch über die Fähigkeit ausgeführt.</p></div></div>
        <div className="kit-slot-grid">
          {slotCard('lightAbilityId', 'Light', true)}
          {slotCard('heavyAbilityId', 'Heavy')}
          {slotCard('ultimateAbilityId', 'Ultimate')}
        </div>
        {active.length === 0 && <div className="empty compact outcome-empty"><strong>Noch keine aktive Startfähigkeit vorhanden.</strong><span>Lege zuerst eine gültige, nicht passive Fähigkeit an.</span></div>}
      </section>

      {record && <section className="panel editor-panel">
        <div className="section-heading"><div><span className="eyebrow">3 · Balancing</span><h2>Theoretische 100-%-Uptime</h2><p>Die drei aktiven Slots werden anhand ihrer kompletten Combo, Ticks, Channel-Dauer und Cooldowns vergleichbar gemacht.</p></div><span className="selection-summary"><strong>{record.balance.theoreticalDps} DPS</strong><small>{record.balance.theoreticalHps} HPS</small></span></div>
        <div className="balance-summary-grid"><div className="field-callout"><strong>Light</strong><span>{record.balance.lightDps} DPS · {record.balance.lightHps} HPS</span></div><div className="field-callout"><strong>Heavy</strong><span>{record.balance.heavyDps} DPS · {record.balance.heavyHps} HPS</span></div><div className="field-callout"><strong>Ultimate</strong><span>{record.balance.ultimateDps} DPS · {record.balance.ultimateHps} HPS</span></div></div>
        {record.balance.notes.map((note)=><div className="field-help" key={note}>{note}</div>)}
      </section>}

      <section className="panel editor-panel">
        <div className="section-heading"><div><span className="eyebrow">4 · Passive Fähigkeiten</span><h2>Dauerhafte Kit-Boni</h2><p>Optional. Alle ausgewählten passiven Buffs wirken, solange dieses Kit aktiv ist.</p></div><span className="selection-count">{draft.passiveAbilityIds.length} ausgewählt</span></div>
        <div className="passive-card-grid">
          {passive.map((ability) => {
            const selected = draft.passiveAbilityIds.includes(ability.id);
            return <label className={`passive-card ${selected ? 'selected' : ''} ${ability.valid ? '' : 'invalid'}`} key={ability.id}><input type="checkbox" checked={selected} onChange={() => togglePassive(ability.id)}/><span><strong>{ability.displayName}</strong><small>{ability.domainKey} · {ability.status}{ability.valid ? '' : ' · ungültig'}</small></span></label>;
          })}
          {passive.length === 0 && <div className="empty compact outcome-empty"><strong>Noch keine passiven Fähigkeiten vorhanden.</strong><span>Passive Fähigkeiten können später ohne Umbau des Kits ergänzt werden.</span></div>}
        </div>
      </section>

      <div className="editor-footer-actions">
        <button className="button ghost" onClick={onBack}>Abbrechen</button>
        <button className="button primary" disabled={busy} onClick={() => void save()}>{busy ? 'Speichert …' : 'Combat Kit speichern'}</button>
      </div>
    </div>
  );
}
